/**
 * ByokKeysPanel.jsx — Settings → AI: per-provider BYOK key management.
 *
 * Lets Studio / Studio+ users store their own OpenAI, Anthropic, and
 * Gemini keys. For each provider that has a key configured, a "Check
 * Balance" button probes the provider's account API and shows the
 * result inline. OpenAI additionally shows a 7-day spend sparkline.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, ExternalLink, Eye, EyeOff, Loader2, RefreshCw,
  Trash2, XCircle, Key, BarChart2, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { cachingPrefsApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const PROVIDERS = [
  {
    id: 'openai',
    label: 'OpenAI',
    prefix: 'sk-',
    placeholder: 'sk-…',
    docs: 'https://platform.openai.com/api-keys',
    console: 'https://platform.openai.com/settings/organization/billing/overview',
    color: 'text-emerald-600',
    balanceSupported: true,
  },
  {
    id: 'anthropic',
    label: 'Anthropic',
    prefix: 'sk-ant-',
    placeholder: 'sk-ant-…',
    docs: 'https://console.anthropic.com/settings/keys',
    console: 'https://console.anthropic.com/settings/plans',
    color: 'text-amber-600',
    balanceSupported: false,
  },
  {
    id: 'gemini',
    label: 'Google Gemini',
    prefix: '',
    placeholder: 'AIza…',
    docs: 'https://aistudio.google.com/app/apikey',
    console: 'https://console.cloud.google.com/billing',
    color: 'text-blue-600',
    balanceSupported: false,
  },
];

/** Tiny SVG sparkline — no external dependency */
function Sparkline({ days, width = 180, height = 36 }) {
  if (!days || days.length === 0) return null;
  const values = days.map((d) => Number(d.cost) || 0);
  const max    = Math.max(...values, 0.001);
  const pts    = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * width;
    const y = height - (v / max) * (height - 4);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const area   = `M0,${height} L${pts.split(' ').map((p, i) => i === 0 ? `${p}` : `L${p}`).join(' ')} L${width},${height} Z`;
  const totalCost = values.reduce((a, b) => a + b, 0).toFixed(3);

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between text-[10.5px] text-muted-foreground">
        <span className="flex items-center gap-1"><BarChart2 className="h-3 w-3" /> 7-day spend</span>
        <span className="font-semibold text-foreground">${totalCost}</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }}>
        <defs>
          <linearGradient id="spk-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.25" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#spk-grad)" />
        <polyline
          points={pts}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {values.map((v, i) => {
          const x = (i / Math.max(values.length - 1, 1)) * width;
          const y = height - (v / max) * (height - 4);
          return <circle key={i} cx={x} cy={y} r="2" fill="hsl(var(--primary))" />;
        })}
      </svg>
      <div className="flex justify-between text-[9.5px] text-muted-foreground px-0.5">
        {days.slice(0, 1).map((d) => (
          <span key="start">{new Date((d.date || 0) * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
        ))}
        <span>today</span>
      </div>
    </div>
  );
}

function ProviderCard({ prov, status, onSaved, onDeleted }) {
  const [keyInput, setKeyInput]   = useState('');
  const [showKey, setShowKey]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [balance, setBalance]     = useState(null);   // null | 'loading' | object
  const [usageData, setUsageData] = useState(null);   // null | 'loading' | { days[] }
  const configured = Boolean(status?.configured);

  const save = async () => {
    if (!keyInput.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/user-llm-keys/${prov.id}`,
        { api_key: keyInput.trim() }, { withCredentials: true });
      toast.success(`${prov.label} key saved.`);
      setKeyInput('');
      onSaved(prov.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to save ${prov.label} key.`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/user-llm-keys/${prov.id}`, { withCredentials: true });
      toast.success(`${prov.label} key removed.`);
      setBalance(null);
      onDeleted(prov.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to remove ${prov.label} key.`);
    } finally {
      setDeleting(false);
    }
  };

  const checkBalance = async () => {
    setBalance('loading');
    try {
      const r = await axios.get(`${API}/user-llm-keys/${prov.id}/balance`,
        { withCredentials: true, timeout: 15000 });
      setBalance(r.data);
    } catch (e) {
      setBalance({ error: e?.response?.data?.detail || 'Could not fetch account info.' });
    }
  };

  const fetchUsage = async () => {
    setUsageData('loading');
    try {
      const r = await axios.get(`${API}/user-llm-keys/openai/usage`,
        { withCredentials: true, timeout: 15000 });
      setUsageData(r.data);
    } catch (e) {
      setUsageData({ days: [], note: e?.response?.data?.detail || 'Could not fetch usage.' });
    }
  };

  const renderBalance = () => {
    if (!balance) return null;
    if (balance === 'loading') {
      return (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking account…
        </div>
      );
    }
    if (balance.error) {
      return (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-destructive">
          <XCircle className="h-3 w-3" /> {balance.error}
        </div>
      );
    }
    // OpenAI credit grants
    if (balance.balance_available && balance.total_available != null) {
      const avail = Number(balance.total_available).toFixed(2);
      const used  = Number(balance.total_used || 0).toFixed(2);
      return (
        <div className="mt-2 space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Credit balance
          </div>
          <div className="text-[12.5px] font-mono-stat text-foreground">
            ${avail} <span className="text-muted-foreground text-[10.5px]">available</span>
          </div>
          <div className="text-[10.5px] text-muted-foreground">
            ${used} used · ${Number(balance.total_granted || 0).toFixed(2)} granted total
          </div>
        </div>
      );
    }
    // OpenAI subscription (pay-as-you-go)
    if (balance.balance_available && balance.plan) {
      return (
        <div className="mt-2 space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> {balance.plan}
          </div>
          {balance.soft_limit_usd != null && (
            <div className="text-[10.5px] text-muted-foreground">
              Soft limit: ${balance.soft_limit_usd} · Hard limit: ${balance.hard_limit_usd}
            </div>
          )}
        </div>
      );
    }
    // No balance API — show validity + console link
    return (
      <div className="mt-2 rounded-md border border-border bg-muted/40 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px]">
          {balance.key_valid !== false
            ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
            : <XCircle className="h-3 w-3 text-destructive" />}
          <span>{balance.key_valid !== false ? 'Key is active' : 'Key rejected by provider'}</span>
        </div>
        {balance.note && (
          <p className="mt-1 text-[10.5px] text-muted-foreground leading-tight">{balance.note}</p>
        )}
        {balance.console_url && (
          <a
            href={balance.console_url}
            target="_blank"
            rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] text-primary hover:underline"
          >
            View account at {prov.label} <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-border bg-background p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key className={cn('h-4 w-4', prov.color)} />
          <span className="text-[13px] font-semibold">{prov.label}</span>
          {configured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              <CheckCircle2 className="h-2.5 w-2.5" /> Configured
            </span>
          )}
        </div>
        <a
          href={prov.docs}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[10.5px] text-muted-foreground hover:text-foreground"
        >
          Get key <ExternalLink className="h-2.5 w-2.5" />
        </a>
      </div>

      {/* Input row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type={showKey ? 'text' : 'password'}
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder={configured ? '••••••••••  (enter new key to replace)' : prov.placeholder}
            className="h-8 pr-8 font-mono text-[12px]"
            data-testid={`byok-key-input-${prov.id}`}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          />
          <button
            type="button"
            onClick={() => setShowKey((v) => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            tabIndex={-1}
          >
            {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !keyInput.trim()}
          className="h-8 text-[12px]"
          data-testid={`byok-save-btn-${prov.id}`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
        {configured && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={checkBalance}
              disabled={balance === 'loading'}
              className="h-8 text-[12px]"
              title={prov.balanceSupported ? 'Check balance' : 'Check key validity'}
              data-testid={`byok-balance-btn-${prov.id}`}
            >
              {balance === 'loading'
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1">{prov.balanceSupported ? 'Balance' : 'Verify'}</span>
            </Button>
            {prov.id === 'openai' && (
              <Button
                size="sm"
                variant="outline"
                onClick={fetchUsage}
                disabled={usageData === 'loading'}
                className="h-8 text-[12px]"
                title="7-day usage history"
                data-testid="byok-usage-btn-openai"
              >
                {usageData === 'loading'
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <BarChart2 className="h-3.5 w-3.5" />}
                <span className="ml-1">Usage</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={remove}
              disabled={deleting}
              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
              title="Remove key"
              data-testid={`byok-delete-btn-${prov.id}`}
            >
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          </>
        )}
      </div>

      {/* Balance / validity result */}
      {renderBalance()}
      {/* OpenAI 7-day usage sparkline */}
      {prov.id === 'openai' && usageData && usageData !== 'loading' && (
        usageData.days?.length > 0
          ? <Sparkline days={usageData.days} />
          : <p className="mt-2 text-[10.5px] text-muted-foreground">{usageData.note || 'No usage data available.'}</p>
      )}
      {prov.id === 'openai' && usageData === 'loading' && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading 7-day spend…
        </div>
      )}
    </div>
  );
}

export default function ByokKeysPanel() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/user-llm-keys`, { withCredentials: true });
      setStatuses(r.data?.providers || []);
    } catch {
      // If the endpoint returns 403 (non-BYOK plan), show a banner
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getStatus = (id) => statuses.find((s) => s.provider === id) || { provider: id, configured: false };

  const handleSaved  = () => load();
  const handleDeleted = () => load();

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-[12px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading key status…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="byok-keys-panel">
      <div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Store your own provider keys to use your own LLM account for all drafting and chat.
          Keys are encrypted at rest and never leave this server.
        </p>
      </div>
      {PROVIDERS.map((prov) => (
        <ProviderCard
          key={prov.id}
          prov={prov}
          status={getStatus(prov.id)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      ))}
      <ByokCachingSection />
    </div>
  );
}

// ── BYOK Prompt Caching toggle ────────────────────────────────────────────────

function ByokCachingSection() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    cachingPrefsApi.get()
      .then((d) => { setEnabled(!!d.byok_caching_enabled); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const toggle = async (v) => {
    setSaving(true);
    try {
      await cachingPrefsApi.update({ byok_caching_enabled: v });
      setEnabled(v);
      toast.success(v ? 'Prompt caching enabled for your BYOK calls' : 'Prompt caching disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update caching preference');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/15 p-4 space-y-3" data-testid="byok-caching-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-500 shrink-0" />
          <div>
            <p className="text-[12.5px] font-semibold text-foreground">Prompt Caching</p>
            <p className="text-[11px] text-muted-foreground">
              Reduces Anthropic input-token costs by ~90% for repeated analysis calls within a 5-minute window
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={toggle}
          disabled={saving || !loaded}
          data-testid="byok-caching-toggle"
          className="shrink-0"
        />
      </div>

      <div className="space-y-1.5 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-amber-600 shrink-0" />
          <span><strong>Anthropic Claude</strong> — ephemeral cache (5 min TTL). Small cache-write fee per entry.</span>
        </div>
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
          <span><strong>OpenAI</strong> — automatic caching (no toggle needed, no extra charge).</span>
        </div>
        <div className="flex items-center gap-1.5">
          <XCircle className="h-3 w-3 text-muted-foreground shrink-0" />
          <span><strong>Google Gemini</strong> — context caching has its own billing model, managed separately.</span>
        </div>
      </div>

      <p className="text-[10.5px] text-muted-foreground border-t border-border/50 pt-2">
        Best for: Arc Walker, Plot Readiness, Continuity Review, and other analysis tasks that
        repeat a large system prompt across multiple calls. Has the most impact when you run
        full-manuscript walkers frequently.
      </p>
    </div>
  );
}
